import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid2";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Line from "../Utilities/Line";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import { APIContext } from "../Contexts/APIContext";
import AdminUserTable from "./User/AdminUserTable";
import { ManageNotifications } from "../Notifications/ManageNotifications";
import UploadSurveyDialog from "./Maintainance/UploadSurveyDialog";
import UploadSitePhotosDialog from "./Maintainance/UploadSitePhotosDialog";

type AdminAction = {
    label: string;
    onClick: () => void;
};

export default function Admin() {
    const { get, post, getCSV, version } = useContext(APIContext);
    const navigate = useNavigate();
    const [details, setDetails] = useState<GetAdminInfoViewModel | null>(null);
    const [openUpload, setOpenUpload] = useState<boolean>(false);
    const [openPhotoUpload, setOpenPhotoUpload] = useState<boolean>(false);
    const [clearingCache, setClearingCache] = useState(false);
    const [cacheStatus, setCacheStatus] = useState<'success' | 'error' | null>(null);

    const clearCache = async () => {
        setClearingCache(true);
        setCacheStatus(null);
        try {
            await post('Admin', 'ClearCache', {}, () => setCacheStatus('success'));
        } catch {
            setCacheStatus('error');
        } finally {
            setClearingCache(false);
        }
    };

    const downloadSurveyList = () => {
        get('Admin', 'GetReleaseVersions', (versions: { id: number; versionName: string }[]) => {
            const name = versions.find(v => v.id === version)?.versionName ?? `${version}`;
            const safeName = name.replace(/[^\w\-. ]+/g, '').trim().replace(/\s+/g, '_');
            getCSV('SurveyListExport', 'surveys.csv', `SurveyList_${safeName}`);
        });
    };

    const adminActions: AdminAction[] = [
        { label: "Manage Users", onClick: () => navigate("/admin/usermanagment") },
        { label: "Licence Calendar", onClick: () => navigate("/admin/calendar") },
        { label: "Manage Organisations", onClick: () => navigate("/admin/manageorganisations") },
        { label: "Upload Survey", onClick: () => setOpenUpload(true) },
        { label: "Invoice Exports", onClick: () => navigate("/admin/invoiceexports") },
        { label: "Usage Reports", onClick: () => navigate("/admin/usagereports") },
        { label: "Device Activity", onClick: () => navigate("/admin/deviceactivity") },
        { label: "Upload Site Photos", onClick: () => setOpenPhotoUpload(true) },
        { label: "Mailchimp Requests", onClick: () => navigate("/admin/mailchimprequests") },
        { label: "Manager User Changes", onClick: () => navigate("/admin/manageruserchanges") },
        { label: "Download Survey List (CSV)", onClick: downloadSurveyList },
    ];

    useEffect(() => {
        get('admin', 'GetAdminInformation', setDetails);
    }, []);

    return (
        details !== null ?
            <Grid container spacing={1.5}>
                <Grid size={12}>
                    <Typography variant="h4" gutterBottom>
                        TRICS Admin
                    </Typography>
                </Grid>
                <Grid size={{ xs: 12, lg: 6 }} component={Paper} sx={{ p: 1.5 }}>
                    <Grid container spacing={1.5}>
                        <Grid>
                            <Typography variant="h6">System Details</Typography>
                        </Grid>
                        <Line />
                        <Grid size={8}>
                            <Typography variant="h6">Current Active Organisation Licences: </Typography>
                        </Grid>
                        <Grid size={4}>
                            <TextField value={details.orgLicencesCount} />
                        </Grid>
                        <Grid size={8}>
                            <Typography variant="h6">Current Active Organisation Sites: </Typography>
                        </Grid>
                        <Grid size={4}>
                            <TextField value={details.orgSitesCount} />
                        </Grid>
                        <Grid size={8}>
                            <Typography variant="h6">Current Active Users: </Typography>
                        </Grid>
                        <Grid size={4}>
                            <TextField value={details.activeMembersCount} />
                        </Grid>
                        <Grid size={12}>
                            <Typography variant="h6" sx={{ mt: 0.5 }}>
                                Admin Actions
                            </Typography>
                        </Grid>
                        {adminActions.map((action) => (
                            <Grid size={{ xs: 6, sm: 4 }} key={action.label}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    size="small"
                                    onClick={action.onClick}
                                    sx={{
                                        minHeight: 44,
                                        px: 1,
                                        py: 0.75,
                                        fontSize: '0.8rem',
                                        whiteSpace: 'normal',
                                        lineHeight: 1.2,
                                    }}
                                >
                                    {action.label}
                                </Button>
                            </Grid>
                        ))}
                        <Grid size={12}>
                            <Typography variant="caption" color="text.secondary">
                                Shortcuts to common administrative tasks and exports.
                            </Typography>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid size={{ xs: 12, lg: 6 }} component={Paper} sx={{ p: 1.5 }}>
                    <AdminUserTable />
                </Grid>
                <Grid size={{ xs: 12, lg: 6 }} component={Paper} sx={{ p: 1.5 }}>
                    <ManageNotifications />
                </Grid>
                {details.isSuperAdmin && <Grid size={{ xs: 12, lg: 6 }} component={Paper} sx={{ p: 1.5 }}>
                    <Typography variant="h6">System tools</Typography>
                    <Typography variant="subtitle2" sx={{ mt: 1 }}>Application cache</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Clear temporary in-memory data if cached counts or filter options do not reflect recent data changes. This does not change database records; the cache rebuilds automatically as the application is used.
                    </Typography>
                    <Button variant="contained" onClick={clearCache} disabled={clearingCache}>
                        {clearingCache ? <CircularProgress size={22} color="inherit" /> : 'Clear cache'}
                    </Button>
                    {cacheStatus && <Alert severity={cacheStatus} sx={{ mt: 1.5 }}>
                        {cacheStatus === 'success' ? 'The application cache has been cleared.' : 'The application cache could not be cleared.'}
                    </Alert>}
                </Grid>}
                <UploadSurveyDialog open={openUpload} onClose={() => setOpenUpload(false)} />
                <UploadSitePhotosDialog open={openPhotoUpload} onClose={() => setOpenPhotoUpload(false)} />
            </Grid>
            :
            <Grid container justifyContent="center">
                <Grid>
                    <CircularProgress />
                </Grid>
            </Grid>
    );
}

export class GetAdminInfoViewModel {
    public orgLicencesCount: number = 0;
    public orgSitesCount: number = 0;
    public activeMembersCount: number = 0;
    public isSuperAdmin: boolean = false;
}
